<script setup lang="ts">
const { t, tm, rt } = useI18n() as any;

const activeExample = ref(0);

const scrollToPricing = () => {
  const element = document.getElementById('pricing');
  if (element) {
    element.scrollIntoView({ behavior: 'smooth' });
  }
};

const apiExamples = ref([
  {
    title: t('viberSurvey.api.restful.title'),
    description: t('viberSurvey.api.restful.description'),
    language: 'JSON',
    code: `{
  "messages": [
    {
      "from": "ALPHANAME",
      "to": "380936670003",
      "type": "viber_survey",
      "text": "Тестовое сообщение",
      "ttl": 30,
      "messageData": {
        "survey": {
          "options": [
            "option1 text",
            "option2 text",
            "option3 text",
            "option4 text",
            "option5 text"
          ]
        }
      }
    }
  ]
}`
  },
  {
    title: t('viberSurvey.api.cascade.title'),
    description: t('viberSurvey.api.cascade.description'),
    language: 'JSON',
    code: String.raw`curl -i -X POST \\
-H "X-Authorization-Key:<токен_доступу>" \\
-H "X-Viber-Auth-Token:<токен_Viber_Bot>" \\
-H "Content-Type:application/json" \\
-d '[
  {
    "id": "21111112",
    "fromName": "GudOk",
    "toPhone": "380936670003",
    "messageType": "viber_survey",
    "ttl": 80,
    "viberMessage": {
      "ttl": 60,
      "text": "Оцініть якість обслуговування в нашому магазині. Ваш відгук допоможе стати кращими 💛",
      "survey": {
        "options": [
          "😍Все було супер!",
          "🙂Добре, але є куди рости",
          "😕Нормально",
          "😞Погано",
          "😡Жахливо"
        ]
      }
    },
    "customerData": {
      "type": "data"
    }
  }
]' \\
'https://api.counterbat.com/api/CascadeMessage/send_message/tg-viber/async'`
  }
]);

const copyToClipboard = (text: string) => {
  navigator.clipboard.writeText(text);
};

// SEO
useHead({
  title: t('viberSurvey.seo.title'),
  meta: [
    {
      name: 'description',
      content: t('viberSurvey.seo.description')
    },
    {
      name: 'keywords',
      content: t('viberSurvey.seo.keywords')
    }
  ]
});
</script>

<template>
  <div class="viber-survey-page">
    <!-- Hero Section -->
    <div class="main-banner">
      <div class="container">
        <div class="banner-content">
          <div class="banner-survey-text">
            <h1 class="banner-title">{{ $t('viberSurvey.hero.title') }}</h1>
            <p class="banner-description">
              {{ $t('viberSurvey.hero.subtitle') }}
            </p>
            <p class="banner-description-secondary">
              {{ $t('viberSurvey.hero.description') }}
            </p>
            <div class="banner-action">
              <NuxtLink to="https://omni.smsbat.com/register-ca" external target="_blank"
                class="nav-link button filled whitespace-nowrap">
                {{ $t('viberSurvey.hero.getStarted') }}
              </NuxtLink>
              <NuxtLink @click="scrollToPricing" class="button outlined cursor-pointer" external>{{
                $t('viberSurvey.hero.viewPricing') }}
              </NuxtLink>
            </div>
          </div>
          <div class="banner-image-container">
            <NuxtImg src="/images/viber_survey.png" class="mt-20 w-full h-auto object-contain" alt="Viber Survey Messages" width="2668" height="2000" format="webp" loading="eager" fetchpriority="high" />
          </div>
        </div>
      </div>
    </div>

    <!-- Global Stats Section -->
    <div class="page-section bg-gray-50">
      <div class="container">
        <div class="section-head">
          <div class="section-head-title">{{ $t('viberSurvey.stats.title') }}</div>
          <div class="section-head-description">{{ $t('viberSurvey.stats.subtitle') }}</div>
        </div>
        <div class="section-content">
          <div class="stats-grid">
            <div class="stat-item">
              <div class="stat-number">63%</div>
              <div class="stat-label">{{ $t('viberSurvey.stats.primeActivity') }}</div>
            </div>
            <div class="stat-item">
              <div class="stat-number">47%</div>
              <div class="stat-label">{{ $t('viberSurvey.stats.women') }}</div>
            </div>
            <div class="stat-item">
              <div class="stat-number">53%</div>
              <div class="stat-label">{{ $t('viberSurvey.stats.men') }}</div>
            </div>
            <div class="stat-item">
              <div class="stat-number">78%</div>
              <div class="stat-label">{{ $t('viberSurvey.stats.mobile') }}</div>
            </div>
          </div>
          <div class="top-countries">
            <h3>{{ $t('viberSurvey.stats.topCountries.title') }}</h3>
            <div class="countries-grid">
              <div class="country-group">
                <h4>{{ $t('viberSurvey.stats.topCountries.cee') }}</h4>
                <div class="country-list">
                  <span class="country-item">Albania 80%</span>
                  <span class="country-item">Bosnia & Herzegovina 98%</span>
                  <span class="country-item">Bulgaria 94%</span>
                  <span class="country-item">Croatia 78%</span>
                  <span class="country-item">Georgia 71%</span>
                  <span class="country-item">Greece 91%</span>
                  <span class="country-item">Hungary 68%</span>
                  <span class="country-item">Macedonia 85%</span>
                  <span class="country-item">Montenegro 96%</span>
                  <span class="country-item">Serbia 97%</span>
                  <span class="country-item">Slovakia 40%</span>
                  <span class="country-item">Slovenia 88%</span>
                  <span class="country-item">Ukraine 98%</span>
                </div>
              </div>
              <div class="country-group">
                <h4>{{ $t('viberSurvey.stats.topCountries.cis') }}</h4>
                <div class="country-list">
                  <span class="country-item">Armenia 93%</span>
                  <span class="country-item">Belarus 86%</span>
                  <span class="country-item">Moldova 82%</span>
                </div>
              </div>
              <div class="country-group">
                <h4>{{ $t('viberSurvey.stats.topCountries.sea') }}</h4>
                <div class="country-list">
                  <span class="country-item">Maldives 76%</span>
                  <span class="country-item">Myanmar 67%</span>
                  <span class="country-item">Nepal 36%</span>
                  <span class="country-item">Philippines 71%</span>
                  <span class="country-item">Sri Lanka 24%</span>
                  <span class="country-item">Vietnam 26%</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Features Section -->
    <div class="page-section">
      <div class="container">
        <div class="section-head">
          <div class="section-head-title">{{ $t('viberSurvey.features.title') }}</div>
          <div class="section-head-description">{{ $t('viberSurvey.features.subtitle') }}</div>
        </div>
        <div class="section-content">
          <div class="features-grid">
            <div class="feature-item">
              <div class="feature-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9 12L11 14L15 10" stroke="#3070b6" stroke-width="2" stroke-linecap="round"
                    stroke-linejoin="round" />
                  <path
                    d="M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
                    stroke="#3070b6" stroke-width="2" />
                </svg>
              </div>
              <h3>{{ $t('viberSurvey.features.interactive.title') }}</h3>
              <p>{{ $t('viberSurvey.features.interactive.description') }}</p>
            </div>

            <div class="feature-item">
              <div class="feature-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="#3070b6" stroke-width="2" stroke-linecap="round"
                    stroke-linejoin="round" />
                  <path d="M2 17L12 22L22 17" stroke="#3070b6" stroke-width="2" stroke-linecap="round"
                    stroke-linejoin="round" />
                  <path d="M2 12L12 17L22 12" stroke="#3070b6" stroke-width="2" stroke-linecap="round"
                    stroke-linejoin="round" />
                </svg>
              </div>
              <h3>{{ $t('viberSurvey.features.feedback.title') }}</h3>
              <p>{{ $t('viberSurvey.features.feedback.description') }}</p>
            </div>

            <div class="feature-item">
              <div class="feature-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M4 16L8 12L12 16L20 8" stroke="#3070b6" stroke-width="2" stroke-linecap="round"
                    stroke-linejoin="round" />
                  <path
                    d="M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
                    stroke="#3070b6" stroke-width="2" />
                </svg>
              </div>
              <h3>{{ $t('viberSurvey.features.analytics.title') }}</h3>
              <p>{{ $t('viberSurvey.features.analytics.description') }}</p>
            </div>

            <div class="feature-item">
              <div class="feature-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2" stroke="#3070b6" stroke-width="2" />
                  <circle cx="8.5" cy="8.5" r="1.5" fill="#3070b6" />
                  <path d="M21 15L16 10L5 21" stroke="#3070b6" stroke-width="2" stroke-linecap="round"
                    stroke-linejoin="round" />
                </svg>
              </div>
              <h3>{{ $t('viberSurvey.features.easy.title') }}</h3>
              <p>{{ $t('viberSurvey.features.easy.description') }}</p>
            </div>

            <div class="feature-item">
              <div class="feature-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" stroke="#3070b6" stroke-width="2"
                    stroke-linecap="round" stroke-linejoin="round" />
                  <path d="M9 12l2 2 4-4" stroke="#3070b6" stroke-width="2" stroke-linecap="round"
                    stroke-linejoin="round" />
                </svg>
              </div>
              <h3>{{ $t('viberSurvey.features.secure.title') }}</h3>
              <p>{{ $t('viberSurvey.features.secure.description') }}</p>
            </div>

            <div class="feature-item">
              <div class="feature-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" stroke="#3070b6" stroke-width="2" stroke-linecap="round"
                    stroke-linejoin="round" />
                </svg>
              </div>
              <h3>{{ $t('viberSurvey.features.fast.title') }}</h3>
              <p>{{ $t('viberSurvey.features.fast.description') }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Benefits Section -->
    <div class="page-section bg-gray-50">
      <div class="container">
        <div class="section-head">
          <div class="section-head-title">{{ $t('viberSurvey.benefits.title') }}</div>
          <div class="section-head-description">{{ $t('viberSurvey.benefits.subtitle') }}</div>
        </div>
        <div class="section-content">
          <div class="benefits-grid">
            <div class="benefit-card">
              <div class="benefit-icon">
                <svg class="mx-auto" width="48" height="48" viewBox="0 0 24 24" fill="none"
                  xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                    stroke="#3070b6" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                </svg>
              </div>
              <h3>{{ $t('viberSurvey.benefits.driveConversations.title') }}</h3>
              <p>{{ $t('viberSurvey.benefits.driveConversations.description') }}</p>
            </div>
            <div class="benefit-card">
              <div class="benefit-icon">
                <svg class="mx-auto" width="48" height="48" viewBox="0 0 24 24" fill="none"
                  xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" stroke="#3070b6" stroke-width="2"
                    stroke-linecap="round" stroke-linejoin="round" />
                </svg>
              </div>
              <h3>{{ $t('viberSurvey.benefits.saveTime.title') }}</h3>
              <p>{{ $t('viberSurvey.benefits.saveTime.description') }}</p>
            </div>
            <div class="benefit-card">
              <div class="benefit-icon">
                <svg class="mx-auto" width="48" height="48" viewBox="0 0 24 24" fill="none"
                  xmlns="http://www.w3.org/2000/svg">
                  <path d="M4 16L8 12L12 16L20 8" stroke="#3070b6" stroke-width="2" stroke-linecap="round"
                    stroke-linejoin="round" />
                  <path
                    d="M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
                    stroke="#3070b6" stroke-width="2" />
                </svg>
              </div>
              <h3>{{ $t('viberSurvey.benefits.boostEngagement.title') }}</h3>
              <p>{{ $t('viberSurvey.benefits.boostEngagement.description') }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- API Examples Section -->
    <div class="page-section bg-gray-50" id="api">
      <div class="container">
        <div class="section-head">
          <div class="section-head-title">{{ $t('viberSurvey.api.title') }}</div>
          <div class="section-head-description">{{ $t('viberSurvey.api.subtitle') }}</div>
        </div>
        <div class="section-content">
          <div class="api-examples">
            <div class="api-tabs">
              <button v-for="(example, index) in apiExamples" :key="index"
                :class="['api-tab', { active: activeExample === index }]" @click="activeExample = index">
                {{ example.title }}
              </button>
            </div>
            <div class="api-content">
              <div class="api-description">
                <h4>{{ apiExamples[activeExample].title }}</h4>
                <p>{{ apiExamples[activeExample].description }}</p>
              </div>
              <div class="code-block">
                <div class="code-header">
                  <span class="code-language">{{ apiExamples[activeExample].language }}</span>
                  <button class="copy-button" @click="copyToClipboard(apiExamples[activeExample].code)">
                    Copy
                  </button>
                </div>
                <pre><code>{{ apiExamples[activeExample].code }}</code></pre>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Cascade API Explanation Section -->
    <div class="page-section">
      <div class="container">
        <div class="section-head">
          <div class="section-head-title">{{ $t('viberSurvey.cascade.title') }}</div>
          <div class="section-head-description">{{ $t('viberSurvey.cascade.subtitle') }}</div>
        </div>
        <div class="section-content">
          <div class="cascade-explanation">
            <div class="cascade-text">
              <p>{{ $t('viberSurvey.cascade.description') }}</p>
              <div class="cascade-benefits">
                <div class="benefit-item">
                  <h4>{{ $t('viberSurvey.cascade.benefits.efficiency.title') }}</h4>
                  <p>{{ $t('viberSurvey.cascade.benefits.efficiency.description') }}</p>
                </div>
                <div class="benefit-item">
                  <h4>{{ $t('viberSurvey.cascade.benefits.reliability.title') }}</h4>
                  <p>{{ $t('viberSurvey.cascade.benefits.reliability.description') }}</p>
                </div>
                <div class="benefit-item">
                  <h4>{{ $t('viberSurvey.cascade.benefits.integration.title') }}</h4>
                  <p>{{ $t('viberSurvey.cascade.benefits.integration.description') }}</p>
                </div>
                <div class="benefit-item">
                  <h4>{{ $t('viberSurvey.cascade.benefits.smartDelivery.title') }}</h4>
                  <p>{{ $t('viberSurvey.cascade.benefits.smartDelivery.description') }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Industry Use Cases Section -->
    <div class="page-section">
      <div class="container">
        <div class="section-head">
          <div class="section-head-title">{{ $t('viberSurvey.industryUseCases.title') }}</div>
          <div class="section-head-description">{{ $t('viberSurvey.industryUseCases.subtitle') }}</div>
        </div>
        <div class="section-content">
          <div class="industry-grid">
            <div class="industry-card">
              <div class="industry-icon">
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M3 3h2l.4 2M7 13h10l4-8H5.4m0 0L7 13m0 0l-2.5 5M7 13l2.5 5m0 0h9" stroke="#3070b6"
                    stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                </svg>
              </div>
              <h3>{{ $t('viberSurvey.industryUseCases.retail.title') }}</h3>
              <ul class="use-cases-list">
                <li>{{ $t('viberSurvey.industryUseCases.retail.reorder') }}</li>
                <li>{{ $t('viberSurvey.industryUseCases.retail.status') }}</li>
                <li>{{ $t('viberSurvey.industryUseCases.retail.returns') }}</li>
                <li>{{ $t('viberSurvey.industryUseCases.retail.locations') }}</li>
              </ul>
            </div>
            <div class="industry-card">
              <div class="industry-icon">
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 2v20M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6" stroke="#3070b6" stroke-width="2"
                    stroke-linecap="round" stroke-linejoin="round" />
                </svg>
              </div>
              <h3>{{ $t('viberSurvey.industryUseCases.banking.title') }}</h3>
              <ul class="use-cases-list">
                <li>{{ $t('viberSurvey.industryUseCases.banking.account') }}</li>
                <li>{{ $t('viberSurvey.industryUseCases.banking.transfer') }}</li>
                <li>{{ $t('viberSurvey.industryUseCases.banking.details') }}</li>
                <li>{{ $t('viberSurvey.industryUseCases.banking.unblock') }}</li>
              </ul>
            </div>
            <div class="industry-card">
              <div class="industry-icon">
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" stroke="#3070b6"
                    stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                </svg>
              </div>
              <h3>{{ $t('viberSurvey.industryUseCases.delivery.title') }}</h3>
              <ul class="use-cases-list">
                <li>{{ $t('viberSurvey.industryUseCases.delivery.schedule') }}</li>
                <li>{{ $t('viberSurvey.industryUseCases.delivery.address') }}</li>
              </ul>
            </div>
            <div class="industry-card">
              <div class="industry-icon">
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M22 12h-4l-3 9L9 3l-3 9H2" stroke="#3070b6" stroke-width="2" stroke-linecap="round"
                    stroke-linejoin="round" />
                </svg>
              </div>
              <h3>{{ $t('viberSurvey.industryUseCases.healthcare.title') }}</h3>
              <ul class="use-cases-list">
                <li>{{ $t('viberSurvey.industryUseCases.healthcare.appointments') }}</li>
                <li>{{ $t('viberSurvey.industryUseCases.healthcare.prescriptions') }}</li>
                <li>{{ $t('viberSurvey.industryUseCases.healthcare.pickup') }}</li>
                <li>{{ $t('viberSurvey.industryUseCases.healthcare.consultation') }}</li>
              </ul>
            </div>
            <div class="industry-card">
              <div class="industry-icon">
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z"
                    stroke="#3070b6" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                </svg>
              </div>
              <h3>{{ $t('viberSurvey.industryUseCases.telco.title') }}</h3>
              <ul class="use-cases-list">
                <li>{{ $t('viberSurvey.industryUseCases.telco.recharge') }}</li>
                <li>{{ $t('viberSurvey.industryUseCases.telco.bills') }}</li>
                <li>{{ $t('viberSurvey.industryUseCases.telco.appointment') }}</li>
                <li>{{ $t('viberSurvey.industryUseCases.telco.upgrade') }}</li>
              </ul>
            </div>
            <div class="industry-card">
              <div class="industry-icon">
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z"
                    stroke="#3070b6" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                  <path d="M3.27 6.96L12 12.01l8.73-5.05M12 22.08V12" stroke="#3070b6" stroke-width="2"
                    stroke-linecap="round" stroke-linejoin="round" />
                </svg>
              </div>
              <h3>{{ $t('viberSurvey.industryUseCases.travel.title') }}</h3>
              <ul class="use-cases-list">
                <li>{{ $t('viberSurvey.industryUseCases.travel.flight') }}</li>
                <li>{{ $t('viberSurvey.industryUseCases.travel.boarding') }}</li>
                <li>{{ $t('viberSurvey.industryUseCases.travel.amend') }}</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Customer Journey Section -->
    <div class="page-section bg-gray-50">
      <div class="container">
        <div class="section-head">
          <div class="section-head-title">{{ $t('viberSurvey.customerJourney.title') }}</div>
          <div class="section-head-description">{{ $t('viberSurvey.customerJourney.subtitle') }}</div>
        </div>
        <div class="section-content">
          <div class="journey-steps">
            <div class="journey-step">
              <div class="step-number">1</div>
              <div class="step-content">
                <h3>{{ $t('viberSurvey.customerJourney.step1.title') }}</h3>
                <p>{{ $t('viberSurvey.customerJourney.step1.description') }}</p>
              </div>
            </div>
            <div class="journey-step">
              <div class="step-number">2</div>
              <div class="step-content">
                <h3>{{ $t('viberSurvey.customerJourney.step2.title') }}</h3>
                <p>{{ $t('viberSurvey.customerJourney.step2.description') }}</p>
              </div>
            </div>
            <div class="journey-step">
              <div class="step-number">3</div>
              <div class="step-content">
                <h3>{{ $t('viberSurvey.customerJourney.step3.title') }}</h3>
                <p>{{ $t('viberSurvey.customerJourney.step3.description') }}</p>
              </div>
            </div>
            <div class="journey-step">
              <div class="step-number">4</div>
              <div class="step-content">
                <h3>{{ $t('viberSurvey.customerJourney.step4.title') }}</h3>
                <p>{{ $t('viberSurvey.customerJourney.step4.description') }}</p>
              </div>
            </div>
            <div class="journey-step">
              <div class="step-number">5</div>
              <div class="step-content">
                <h3>{{ $t('viberSurvey.customerJourney.step5.title') }}</h3>
                <p>{{ $t('viberSurvey.customerJourney.step5.description') }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Best Practices Section -->
    <div class="page-section">
      <div class="container">
        <div class="section-head">
          <div class="section-head-title">{{ $t('viberSurvey.bestPractices.title') }}</div>
          <div class="section-head-description">{{ $t('viberSurvey.bestPractices.subtitle') }}</div>
        </div>
        <div class="section-content">
          <div class="practices-grid">
            <div class="practice-item">
              <div class="practice-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" stroke="#3070b6" stroke-width="2"
                    stroke-linecap="round" stroke-linejoin="round" />
                </svg>
              </div>
              <h4>{{ $t('viberSurvey.bestPractices.short.title') }}</h4>
              <p>{{ $t('viberSurvey.bestPractices.short.description') }}</p>
            </div>
            <div class="practice-item">
              <div class="practice-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    stroke="#3070b6" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                </svg>
              </div>
              <h4>{{ $t('viberSurvey.bestPractices.emojis.title') }}</h4>
              <p>{{ $t('viberSurvey.bestPractices.emojis.description') }}</p>
            </div>
            <div class="practice-item">
              <div class="practice-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    stroke="#3070b6" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                </svg>
              </div>
              <h4>{{ $t('viberSurvey.bestPractices.scannable.title') }}</h4>
              <p>{{ $t('viberSurvey.bestPractices.scannable.description') }}</p>
            </div>
            <div class="practice-item">
              <div class="practice-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"
                    stroke="#3070b6" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                </svg>
              </div>
              <h4>{{ $t('viberSurvey.bestPractices.instructions.title') }}</h4>
              <p>{{ $t('viberSurvey.bestPractices.instructions.description') }}</p>
            </div>
            <div class="practice-item">
              <div class="practice-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2M9 11a4 4 0 100-8 4 4 0 000 8zM23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75"
                    stroke="#3070b6" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                </svg>
              </div>
              <h4>{{ $t('viberSurvey.bestPractices.context.title') }}</h4>
              <p>{{ $t('viberSurvey.bestPractices.context.description') }}</p>
            </div>
            <div class="practice-item">
              <div class="practice-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9 19c-5 0-7-2-7-5s2-5 7-5 7 2 7 5-2 5-7 5zM15 13l3 3 3-3" stroke="#3070b6" stroke-width="2"
                    stroke-linecap="round" stroke-linejoin="round" />
                </svg>
              </div>
              <h4>{{ $t('viberSurvey.bestPractices.test.title') }}</h4>
              <p>{{ $t('viberSurvey.bestPractices.test.description') }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Pricing Section -->
    <div class="page-section" id="pricing">
      <div class="container">
        <div class="section-head">
          <div class="section-head-title">{{ $t('viberSurvey.pricing.title') }}</div>
          <div class="section-head-description">{{ $t('viberSurvey.pricing.subtitle') }}</div>
        </div>
        <div class="section-content">
          <div class="pricing-explanation">
            <div class="pricing-info">
              <h3>{{ $t('viberSurvey.pricing.explanation.title') }}</h3>
              <p>{{ $t('viberSurvey.pricing.explanation.description') }}</p>
              <div class="pricing-types">
                <div class="pricing-type">
                  <h4>{{ $t('viberSurvey.pricing.explanation.transactional.title') }}</h4>
                  <p>{{ $t('viberSurvey.pricing.explanation.transactional.description') }}</p>
                </div>
                <div class="pricing-type">
                  <h4>{{ $t('viberSurvey.pricing.explanation.international.title') }}</h4>
                  <p>{{ $t('viberSurvey.pricing.explanation.international.description') }}</p>
                </div>
              </div>
            </div>
          </div>
          <div class="pricing-table-container">
            <ViberSurveyRatesTable />
          </div>
        </div>
      </div>
    </div>

    <!-- CTA Section -->
    <div class="page-section bg-gradient bottom-banner">
      <div class="container">
        <div class="section-head">
          <div class="section-head-title">{{ $t('viberSurvey.cta.title') }}</div>
          <div class="section-head-description text-gray-600">{{ $t('viberSurvey.cta.description') }}</div>
        </div>
        <div class="section-content">
          <div class="banner-action flex flex-wrap items-center justify-center gap-4">
            <NuxtLink to="https://omni.smsbat.com/register-ca" external target="_blank" class="nav-link button filled">
              <p>{{ $t('viberSurvey.cta.getStarted') }}</p>
            </NuxtLink>
            <NuxtLink to="https://help.smsbat.com/uk/hc/1780048863/1/api?category_id=3#viber_survey" external
              target="_blank" class="button outlined">
              <p>{{ $t('viberSurvey.cta.viewDocs') }}</p>
            </NuxtLink>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.bg-gradient {
  background: linear-gradient(135deg, #4f46e5 0%, #764ba2 100%);
  color: white;

  .section-head-title {
    color: white;
  }

  .text-gray-600 {
    color: rgba(255, 255, 255, 0.8);
  }

  .button.outlined {
    background: transparent;
  }
}

.viber-survey-page {
  min-height: 100vh;
}

.main-banner {
  padding: 80px 0;
  min-height: 80vh;
  display: flex;
  align-items: center;
}

.banner-content {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 60px;
  align-items: center;
}

.banner-survey-text h1 {
  font-size: 3rem;
  font-weight: 800;
  margin-bottom: 20px;
  line-height: 1.2;
}

.banner-description {
  font-size: 1.3rem;
  margin-bottom: 15px;
  opacity: 0.9;
}

.banner-description-secondary {
  font-size: 1.1rem;
  margin-bottom: 40px;
  opacity: 0.8;
}

.banner-buttons {
  display: flex;
  gap: 20px;
  flex-wrap: wrap;
}

.btn {
  padding: 15px 30px;
  border-radius: 8px;
  font-weight: 600;
  text-decoration: none;
  display: inline-block;
  transition: all 0.3s ease;
  border: 2px solid transparent;
}

.btn-primary {
  background: white;
  color: #4f46e5;
  border-color: white;
}

.btn-primary:hover {
  background: #f8f9fa;
  transform: translateY(-2px);
}

.btn-outline {
  background: transparent;
  color: white;
  border-color: white;
}

.btn-outline:hover {
  background: white;
  color: #4f46e5;
}

.btn-large {
  padding: 18px 40px;
  font-size: 1.1rem;
}

.page-section {
  padding: 80px 0;
}

.section-head {
  text-align: center;
  margin-bottom: 60px;
}

.section-head-title {
  font-size: 2.5rem;
  font-weight: 800;
  color: #2d3748;
  margin-bottom: 20px;
  line-height: 1.2;
}

.section-head-description {
  font-size: 1.2rem;
  max-width: 600px;
  margin: 0 auto;
}

.features-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 40px;
  max-width: 1200px;
  margin: 0 auto;
}

.feature-item {
  text-align: center;
  padding: 40px 30px;
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
}

.feature-item:hover {
  transform: translateY(-5px);
  box-shadow: 0 12px 40px rgba(123, 81, 157, 0.15);
}

.feature-icon {
  width: 80px;
  height: 80px;
  margin: 0 auto 25px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(123, 81, 157, 0.1);
  border-radius: 20px;
}

.feature-item h3 {
  font-size: 1.4rem;
  font-weight: 700;
  color: #2d3748;
  margin-bottom: 15px;
}

.feature-item p {
  color: #718096;
  line-height: 1.6;
  font-size: 1rem;
}

.bg-gray-50 {
  background: #f8f9fa;
}

.api-examples {
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  max-width: 1000px;
  margin: 0 auto;
}

.api-tabs {
  display: flex;
  background: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
}

.api-tab {
  flex: 1;
  padding: 15px 20px;
  background: none;
  border: none;
  cursor: pointer;
  font-weight: 600;
  color: #6c757d;
  transition: all 0.3s ease;
}

.api-tab.active {
  background: white;
  color: #4f46e5;
  border-bottom: 2px solid #4f46e5;
}

.api-tab:hover:not(.active) {
  background: #e9ecef;
}

.api-content {
  padding: 30px;
}

.api-description {
  margin-bottom: 25px;
}

.api-description h4 {
  font-size: 1.3rem;
  font-weight: 700;
  color: #2d3748;
  margin-bottom: 10px;
}

.api-description p {
  color: #718096;
  line-height: 1.6;
}

.code-block {
  background: #f8f9fa;
  border-radius: 8px;
  overflow: hidden;
}

.code-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 15px;
  background: #e9ecef;
  border-bottom: 1px solid #dee2e6;
}

.code-language {
  font-weight: 600;
  color: #495057;
}

.copy-button {
  background: #4f46e5;
  color: white;
  border: none;
  padding: 5px 12px;
  border-radius: 4px;
  font-size: 0.8rem;
  cursor: pointer;
  transition: all 0.3s ease;
}

.copy-button:hover {
  background: #5a67d8;
}

.code-block pre {
  margin: 0;
  padding: 20px;
  overflow-x: auto;
}

.code-block code {
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 0.9rem;
  line-height: 1.5;
  color: #2d3748;
}

.cascade-explanation {
  background: white;
  border-radius: 12px;
  padding: 40px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  max-width: 1000px;
  margin: 0 auto;
}

.cascade-text p {
  font-size: 1.1rem;
  line-height: 1.7;
  color: #4a5568;
  margin-bottom: 30px;
}

.cascade-benefits {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 30px;
}

.benefit-item {
  padding: 25px;
  background: #f8f9fa;
  border-radius: 8px;
  border-left: 4px solid #7B519D;
}

.benefit-item h4 {
  font-size: 1.2rem;
  font-weight: 700;
  color: #2d3748;
  margin-bottom: 10px;
}

.benefit-item p {
  color: #718096;
  line-height: 1.6;
  margin: 0;
}

.pricing-explanation {
  background: white;
  border-radius: 12px;
  padding: 40px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  max-width: 1000px;
  margin: 0 auto 40px;
}

.pricing-info h3 {
  font-size: 1.5rem;
  font-weight: 700;
  color: #2d3748;
  margin-bottom: 15px;
  text-align: center;
}

.pricing-info p {
  font-size: 1.1rem;
  line-height: 1.7;
  color: #4a5568;
  margin-bottom: 30px;
  text-align: center;
}

.pricing-types {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 30px;
}

.pricing-type {
  padding: 25px;
  background: #f8f9fa;
  border-radius: 8px;
  border-left: 4px solid #3070b6;
}

.pricing-type h4 {
  font-size: 1.2rem;
  font-weight: 700;
  color: #2d3748;
  margin-bottom: 10px;
}

.pricing-type p {
  color: #718096;
  line-height: 1.6;
  margin: 0;
  text-align: left;
}

.pricing-table-container {
  overflow-x: auto;
  max-width: 100%;
}

.pricing-table {
  width: 100%;
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border-collapse: collapse;
}

.pricing-table th {
  background: #f8f9fa;
  padding: 20px;
  text-align: left;
  font-weight: 700;
  color: #2d3748;
  border-bottom: 2px solid #e9ecef;
}

.pricing-table td {
  padding: 15px 20px;
  border-bottom: 1px solid #e9ecef;
  color: #4a5568;
}

.pricing-table tr:hover {
  background: #f8f9fa;
}

/* Responsive Design */
@media (max-width: 768px) {
  .bottom-banner {
    margin-bottom: -40px;
  }

  .banner-content {
    grid-template-columns: 1fr;
    gap: 40px;
    text-align: center;
  }

  .banner-survey-text h1 {
    font-size: 2.2rem;
  }

  .banner-description {
    font-size: 1.1rem;
  }

  .banner-description-secondary {
    font-size: 1rem;
  }

  .banner-buttons {
    justify-content: center;
  }

  .features-grid {
    grid-template-columns: 1fr;
    gap: 30px;
  }

  .feature-item {
    padding: 30px 20px;
  }

  .api-tabs {
    flex-direction: column;
  }

  .api-tab {
    border-bottom: 1px solid #e9ecef;
  }

  .api-tab.active {
    border-bottom: none;
  }

  .cascade-benefits {
    grid-template-columns: 1fr;
  }

  .pricing-table th,
  .pricing-table td {
    padding: 10px 15px;
  }

  .cta-buttons {
    flex-direction: column;
    align-items: center;
  }
}

@media (max-width: 480px) {
  .main-banner {
    padding: 60px 0;
  }

  .banner-survey-text h1 {
    font-size: 1.8rem;
  }

  .section-head-title {
    font-size: 2rem;
  }

  .feature-item {
    padding: 25px 15px;
  }

  .pricing-table th,
  .pricing-table td {
    padding: 8px 12px;
  }
}

/* Global Stats Section */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 2rem;
  margin-bottom: 3rem;
}

.stat-item {
  text-align: center;
  padding: 1.5rem;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

.stat-number {
  font-size: 2.5rem;
  font-weight: bold;
  color: #3070b6;
  margin-bottom: 0.5rem;
}

.stat-label {
  color: #6c757d;
  font-size: 0.9rem;
  line-height: 1.4;
}

.top-countries h3 {
  color: #2c3e50;
  margin-bottom: 2rem;
  text-align: center;
  font-size: 1.5rem;
}

.countries-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
}

.country-group h4 {
  color: #3070b6;
  margin-bottom: 1rem;
  font-size: 1.1rem;
}

.country-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.country-item {
  color: #6c757d;
  font-size: 0.9rem;
  padding: 0.5rem;
  background: white;
  border-radius: 6px;
  border-left: 3px solid #3070b6;
}

@media (max-width: 768px) {
  .stats-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 1rem;
  }

  .countries-grid {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
}

/* Benefits Section */
.benefits-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
}

.benefit-card {
  text-align: center;
  padding: 2rem;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease;
}

.benefit-card:hover {
  transform: translateY(-5px);
}

.benefit-icon {
  margin-bottom: 1rem;
}

.benefit-card h3 {
  color: #2c3e50;
  margin-bottom: 1rem;
  font-size: 1.2rem;
}

.benefit-card p {
  color: #6c757d;
  line-height: 1.6;
}

@media (max-width: 768px) {
  .benefits-grid {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
}

/* Industry Use Cases Section */
.industry-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
}

.industry-card {
  padding: 2rem;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease;
}

.industry-card:hover {
  transform: translateY(-5px);
}

.industry-icon {
  margin-bottom: 1rem;
}

.industry-card h3 {
  color: #2c3e50;
  margin-bottom: 1rem;
  font-size: 1.2rem;
}

.use-cases-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.use-cases-list li {
  color: #6c757d;
  padding: 0.5rem 0;
  border-bottom: 1px solid #f0f0f0;
  position: relative;
  padding-left: 1.5rem;
}

.use-cases-list li:before {
  content: "✓";
  position: absolute;
  left: 0;
  color: #3070b6;
  font-weight: bold;
}

.use-cases-list li:last-child {
  border-bottom: none;
}

@media (max-width: 768px) {
  .industry-grid {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
}

/* Customer Journey Section */
.journey-steps {
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.journey-step {
  display: flex;
  align-items: center;
  gap: 2rem;
  padding: 2rem;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

.step-number {
  width: 60px;
  height: 60px;
  background: #3070b6;
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  font-weight: bold;
  flex-shrink: 0;
}

.step-content h3 {
  color: #2c3e50;
  margin-bottom: 0.5rem;
  font-size: 1.2rem;
}

.step-content p {
  color: #6c757d;
  line-height: 1.6;
  margin: 0;
}

@media (max-width: 768px) {
  .journey-step {
    flex-direction: column;
    text-align: center;
    gap: 1rem;
  }

  .step-number {
    width: 50px;
    height: 50px;
    font-size: 1.2rem;
  }
}

/* Best Practices Section */
.practices-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
}

.practice-item {
  padding: 1.5rem;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease;
}

.practice-item:hover {
  transform: translateY(-5px);
}

.practice-icon {
  margin-bottom: 1rem;
}

.practice-item h4 {
  color: #2c3e50;
  margin-bottom: 0.5rem;
  font-size: 1.1rem;
}

.practice-item p {
  color: #6c757d;
  line-height: 1.6;
  margin: 0;
  font-size: 0.9rem;
}

@media (max-width: 768px) {
  .practices-grid {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
}

.banner-action {
  display: flex;
  gap: 20px;
  align-items: center;
  flex-wrap: wrap;
  margin-top: 30px;
}

.banner-action .button {
  min-width: fit-content;
  white-space: nowrap;
  padding: 0 24px;
}

@media (max-width: 768px) {
  .main-banner {
    .banner-title {
      margin: 0 auto;
      text-align: center;
    }

    .banner-description {
      margin: 0 auto;
      text-align: center;
    }
  }
}
</style>
